import { useState, useEffect, useRef } from "react";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import "./swagger-ui-mds.css";

const getSpecUrl = () =>
  process.env.REACT_APP_OPENAPI_SPEC_URL ||
  `${process.env.REACT_APP_API_BASE_URL || ""}/api-docs/openapi.json`;

const getServerBaseUrl = () => {
  if (process.env.REACT_APP_API_SERVER_URL)
    return process.env.REACT_APP_API_SERVER_URL.replace(/\/$/, "");
  const specUrl = process.env.REACT_APP_OPENAPI_SPEC_URL;
  if (specUrl) {
    try {
      const u = new URL(specUrl);
      const path =
        u.pathname.replace(
          /\/(v\d+\/)?(api-docs|openapi\.(json|yaml))(\?.*)?$/i,
          "",
        ) || "";
      return `${u.origin}${path}`.replace(/\/$/, "");
    } catch (_) {}
  }
  if (process.env.REACT_APP_API_BASE_URL)
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, "");
  return null;
};

const SPEC_LINK_INJECTED = "data-swagger-mds-spec-link";

const applyServerToSpec = (spec, serverBaseUrl) => {
  if (!serverBaseUrl || !spec) return spec;
  return {
    ...spec,
    servers: [{ url: serverBaseUrl }],
  };
};

function SwaggerView({ accessToken, requireAuth = false, tokenError = false }) {
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState(null);
  const url = getSpecUrl();
  const tokenRef = useRef(accessToken);
  tokenRef.current = accessToken;

  useEffect(() => {
    if (
      !process.env.REACT_APP_OPENAPI_SPEC_URL &&
      !process.env.REACT_APP_API_BASE_URL
    ) {
      setError(
        "Set REACT_APP_OPENAPI_SPEC_URL or REACT_APP_API_BASE_URL in .env",
      );
      return;
    }
    if (requireAuth && !accessToken) return;
    const headers = {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    };
    fetch(url, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load spec: ${res.status}`);
        return res.json();
      })
      .then((json) => setSpec(applyServerToSpec(json, getServerBaseUrl())))
      .catch((err) => setError(err.message));
  }, [url, accessToken, requireAuth]);

  const requestInterceptor = (req) => {
    if (accessToken && req.headers) {
      req.headers.Authorization = `Bearer ${accessToken}`;
    }
    return req;
  };

  const [specLinkLoading, setSpecLinkLoading] = useState(false);
  const handleOpenSpecWithAuth = (e) => {
    e.preventDefault();
    if (specLinkLoading) return;
    const token = tokenRef.current;
    setSpecLinkLoading(true);
    const linkEl = document.querySelector(
      ".swagger-mds-wrap .swagger-mds-spec-link",
    );
    if (linkEl) linkEl.textContent = "Opening…";
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    fetch(url, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(
          new Blob([blob], { type: "application/json" }),
        );
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      })
      .catch(() => {})
      .finally(() => {
        setSpecLinkLoading(false);
        const el = document.querySelector(
          ".swagger-mds-wrap .swagger-mds-spec-link",
        );
        if (el) el.textContent = "OpenAPI spec (JSON)";
      });
  };

  const getBearerSchemeName = (s) => {
    if (!s?.components?.securitySchemes) return null;
    return Object.keys(s.components.securitySchemes).find((k) => {
      const scheme = s.components.securitySchemes[k];
      return (
        scheme?.type === "http" &&
        (scheme?.scheme === "bearer" || scheme?.scheme === "Bearer")
      );
    });
  };

  const injectSpecLinkInsideInfo = () => {
    const wrap = document.querySelector(".swagger-mds-wrap");
    if (!wrap?.querySelector(`[${SPEC_LINK_INJECTED}]`)) {
      const info = wrap?.querySelector(".swagger-ui .info");
      if (info) {
        const div = document.createElement("div");
        div.setAttribute(SPEC_LINK_INJECTED, "");
        div.className = "swagger-mds-spec-link-inner";
        const a = document.createElement("a");
        a.href = url;
        a.className = "swagger-mds-spec-link";
        a.rel = "noopener noreferrer";
        a.textContent = "OpenAPI spec (JSON)";
        a.addEventListener("click", handleOpenSpecWithAuth);
        div.appendChild(a);
        info.insertBefore(div, info.firstChild);
      }
    }
  };

  const onSwaggerComplete = (system) => {
    if (accessToken && system?.preauthorizeApiKey) {
      const schemeName = getBearerSchemeName(spec) || "bearerAuth";
      system.preauthorizeApiKey(schemeName, accessToken);
    }
    setTimeout(injectSpecLinkInsideInfo, 0);
  };

  if (error) {
    return (
      <div className="swagger-mds-error" role="alert">
        <p>{error}</p>
        <p>Set REACT_APP_OPENAPI_SPEC_URL or REACT_APP_API_BASE_URL in .env</p>
      </div>
    );
  }

  if (requireAuth && tokenError) {
    return (
      <div className="swagger-mds-error" role="alert">
        <p>Could not get access token. Please sign out and sign in again.</p>
      </div>
    );
  }

  if (!spec) {
    return (
      <div className="swagger-mds-loading">
        <p>
          {requireAuth && !accessToken
            ? "Acquiring token…"
            : "Loading API spec…"}
        </p>
      </div>
    );
  }

  return (
    <div className="swagger-mds-wrap">
      <SwaggerUI
        spec={spec}
        requestInterceptor={requestInterceptor}
        onComplete={onSwaggerComplete}
      />
    </div>
  );
}

export default SwaggerView;
